/*
 * Lucky Muffin — email sign-up: popup (sections/lm-popup.liquid) and footer
 * form (sections/lm-footer.liquid). Both sections load this file; the guard
 * below makes the second copy a no-op.
 * Specs: inputs/2026-09-28_Email_Signup-Popup-Requirements.md (+ footer follow-up)
 *
 * Shared
 * - Submit: Klaviyo client subscriptions API, in place, no reload. Falls back
 *   to the native Shopify form post if the key or list is missing.
 * - One "subscribed" flag (localStorage) for both forms; a footer sign-up also
 *   disarms a pending popup on the same page (lm:subscribed event).
 * - Events go to window.dataLayer and Shopify.analytics.publish (read by the
 *   GA4 custom pixel, scripts/ga4-custom-pixel.js). Never includes the email.
 *   <form>_submit, <form>_success { mode }, <form>_error { reason }.
 * - Typo hint: a mistyped common domain (gmial.com, yahoo.con …) gets an
 *   inline "Did you mean …?" with a one-tap fix. It never blocks the submit.
 *
 * Popup
 * - Triggers: touch → first of scroll depth / time, never before min_seconds.
 *   Desktop (hover + fine pointer) → exit intent, or the same rule if exit
 *   intent is off. One trigger per page view; never re-arms after a dismiss.
 * - Suppression: subscribed (forever), dismissed (frequency_days), arrived from
 *   an email this session (utm_source=klaviyo / utm_medium=email / _kx), or
 *   already identified by Klaviyo (__kla_id cookie, then klaviyo.isIdentified()).
 */
(function () {
  // ---- Pure helpers (no DOM). Exported for scripts/tests. ----
  // Second-level typos of the big providers, checked after the TLD fix.
  var DOMAIN_TYPOS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com',
    'gnail.com': 'gmail.com', 'gmsil.com': 'gmail.com',
    'yhoo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com',
    'outlok.com': 'outlook.com'
  };
  // The suggested address, or '' when the domain looks fine.
  function suggestEmail(email) {
    var at = email.lastIndexOf('@');
    if (at < 1) return '';
    var domain = email.slice(at + 1).toLowerCase();
    var fixed = domain
      .replace(/\.(?:cim|con|cpm|comm|om)$/, '.com')
      .replace(/(\.(?:com|net|org))\1$/, '$1'); // name@x.net.net → x.net
    fixed = DOMAIN_TYPOS[fixed] || fixed;
    return fixed === domain ? '' : email.slice(0, at + 1) + fixed;
  }
  var api = { suggestEmail: suggestEmail };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;

  if (window.__lmSignup) return;
  window.__lmSignup = true;

  var KEY_DISMISSED = 'lm-popup-dismissed-at';
  var KEY_SUBSCRIBED = 'lm-popup-subscribed'; // shared by popup + footer
  var KEY_VARIANT = 'lm-popup-variant';
  var KEY_UTM = 'lm-popup-utm';
  var KEY_FROM_EMAIL = 'lm-popup-from-email';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }
  function get(store, k) {
    try { return window[store].getItem(k); } catch (e) { return null; }
  }
  function set(store, k, v) {
    try { window[store].setItem(k, v); } catch (e) {}
  }

  // ---- Attribution: UTMs from this visit (first touch in the session) ----
  var params = new URLSearchParams(window.location.search);
  var utm = {};
  ['source', 'medium', 'campaign', 'content'].forEach(function (k) {
    var v = params.get('utm_' + k);
    if (v) utm[k] = v.slice(0, 100);
  });
  var fromEmail = /^klaviyo$/i.test(utm.source || '') || /^email$/i.test(utm.medium || '') || params.has('_kx');
  if (fromEmail) set('sessionStorage', KEY_FROM_EMAIL, '1');
  var storedUtm = get('sessionStorage', KEY_UTM);
  if (storedUtm) {
    try { utm = JSON.parse(storedUtm) || {}; } catch (e) {}
  } else if (Object.keys(utm).length) {
    set('sessionStorage', KEY_UTM, JSON.stringify(utm));
  }

  function track(name, data) {
    if (designMode) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, data));
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) {}
  }

  // detail: { root, email } when a form on this page just succeeded, so the
  // other sign-up form can switch to its success state too.
  function markSubscribed(detail) {
    set('localStorage', KEY_SUBSCRIBED, '1');
    document.dispatchEvent(new CustomEvent('lm:subscribed', { detail: detail || {} }));
  }

  // Swap a sign-up root to its success block. {email} is filled via textContent.
  function showSuccess(root, form, success, email) {
    success.querySelectorAll('[data-template]').forEach(function (el) {
      el.textContent = el.getAttribute('data-template').split('{email}').join(email);
    });
    form.hidden = true;
    success.hidden = false;
  }

  /*
   * Wire a sign-up form by delegation: one document-level submit listener that
   * resolves the form's parts at submit time, so it keeps working when the
   * section is re-rendered (theme editor setting changes, `theme dev` hot
   * reload) and the original form element is replaced. cfg:
   *   selector (root with data-klaviyo-key/-list, data-error-invalid/-network),
   *   errorSel, successSel (hidden block with [data-template] text),
   *   customSource, props (extra profile properties), onSubmit(),
   *   onSuccess(root), onError(reason)
   * The typo hint is a [data-lm-signup-suggest="Did you mean {email}?"] live
   * region inside the root; it stays empty until there's something to suggest.
   */
  function wireForm(cfg) {
    // Shopify's form spam protection (the captcha-bootstrap script in
    // content_for_header) guards every `customer` form on /contact. On submit it
    // re-posts the form itself with form.submit(), which skips our
    // preventDefault, so the shopper gets Shopify's reload/challenge page
    // instead of our in-place success. Its opt-out is data-nocaptcha="true".
    // Set it whenever we'll send to Klaviyo ourselves. When Klaviyo isn't
    // configured, the native post (and its captcha) stay intact.
    function claim(form) {
      var root = form.closest(cfg.selector);
      var d = root && root.dataset;
      if (!d || !d.klaviyoKey || !d.klaviyoList || !window.fetch) return;
      form.noValidate = true; // inline errors instead of the browser bubble
      form.setAttribute('data-nocaptcha', 'true');
    }
    document.querySelectorAll(cfg.selector + ' form').forEach(claim);

    // The other sign-up form on this page succeeded → show ours as done too
    // (no focus move, no analytics event; this form wasn't used).
    document.addEventListener('lm:subscribed', function (e) {
      var detail = e.detail || {};
      if (!detail.root || !detail.email) return;
      var root = document.querySelector(cfg.selector);
      if (!root || root === detail.root) return;
      var form = root.querySelector('form');
      var success = root.querySelector(cfg.successSel);
      if (form && success && !form.hidden) showSuccess(root, form, success, detail.email);
    });
    // Re-rendered sections (theme editor, theme dev hot reload): claim on first
    // focus, before the captcha script loads and binds.
    document.addEventListener('focusin', function (e) {
      var form = e.target.closest && e.target.closest(cfg.selector + ' form');
      if (form) claim(form);
    }, true);

    // ---- Typo hint: suggest only, never block the submit ----
    function updateHint(root) {
      var hint = root.querySelector('[data-lm-signup-suggest]');
      var input = root.querySelector('form input[type="email"]');
      if (!hint || !input) return;
      var suggestion = suggestEmail(input.value.trim());
      if (suggestion === (hint.getAttribute('data-suggestion') || '')) return; // don't re-announce
      hint.setAttribute('data-suggestion', suggestion);
      hint.textContent = '';
      if (!suggestion) return;
      var parts = (hint.getAttribute('data-lm-signup-suggest') || '{email}').split('{email}');
      var fix = document.createElement('button');
      fix.type = 'button';
      fix.className = 'lm-signup-suggest__fix';
      fix.setAttribute('data-lm-signup-fix', '');
      fix.textContent = suggestion;
      hint.appendChild(document.createTextNode(parts[0]));
      hint.appendChild(fix);
      hint.appendChild(document.createTextNode(parts.slice(1).join(suggestion)));
    }
    document.addEventListener('input', function (e) {
      var root = e.target.closest && e.target.closest(cfg.selector);
      if (root && e.target.type === 'email') updateHint(root);
    });
    document.addEventListener('click', function (e) {
      var fix = e.target.closest && e.target.closest('[data-lm-signup-fix]');
      var root = fix && fix.closest(cfg.selector);
      var input = root && root.querySelector('form input[type="email"]');
      if (!input) return;
      input.value = fix.textContent;
      updateHint(root); // the fixed address has nothing to suggest → clears
      input.focus();
    });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      var root = form && form.closest && form.closest(cfg.selector);
      if (!root) return;
      var d = root.dataset;
      var input = form.querySelector('input[type="email"]');
      var errorEl = root.querySelector(cfg.errorSel);
      var success = root.querySelector(cfg.successSel);
      var button = form.querySelector('button[type="submit"]');
      if (!input || !errorEl || !success || !button) return; // e.g. the posted-success state
      var baseDescribedBy = (input.getAttribute('aria-describedby') || '').replace(errorEl.id, '').trim();

      function showError(msg) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', (errorEl.id + ' ' + baseDescribedBy).trim());
      }
      function clearError() {
        errorEl.hidden = true;
        errorEl.textContent = '';
        input.removeAttribute('aria-invalid');
        if (baseDescribedBy) input.setAttribute('aria-describedby', baseDescribedBy);
        else input.removeAttribute('aria-describedby');
      }
      function busy(on) {
        button.disabled = on;
        button.setAttribute('aria-busy', on ? 'true' : 'false');
      }
      function succeed(email) {
        showSuccess(root, form, success, email);
        success.focus();
        markSubscribed({ root: root, email: email });
        cfg.onSuccess(root);
      }

      var key = d.klaviyoKey, list = d.klaviyoList;
      var email = input.value.trim();
      if (!EMAIL_RE.test(email)) {
        e.preventDefault();
        showError(d.errorInvalid);
        input.focus();
        cfg.onError('invalid_email');
        return;
      }
      if (!key || !list || !window.fetch) return; // native Shopify post fallback
      e.preventDefault();
      clearError();

      var props = Object.assign({ signup_page: window.location.pathname }, cfg.props);
      Object.keys(utm).forEach(function (k) { props['signup_utm_' + k] = utm[k]; });
      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: cfg.customSource,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  properties: props
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      };

      cfg.onSubmit();
      busy(true);
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        // The explicit `subscriptions` consent field needs a recent revision;
        // 2024-10-15 rejects it with a 400 on every submit.
        headers: { 'Content-Type': 'application/json', revision: '2026-10-15' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        // 202 for new and existing subscribers alike ("already subscribed" = success).
        if (r.ok) { busy(false); succeed(email); return; }
        return r.json().catch(function () { return {}; }).then(function (body) {
          busy(false);
          // http_<status> even when Klaviyo rejects the email itself, so GA4
          // keeps our own check (invalid_email) apart from Klaviyo's.
          cfg.onError('http_' + r.status);
          var err = (body.errors || [])[0] || {};
          var pointer = (err.source && err.source.pointer) || '';
          // Only blame the visitor's typing when Klaviyo points at the email.
          if (r.status === 400 && /email/.test(pointer)) { showError(d.errorInvalid); return; }
          if (window.console) console.warn('[lm-signup] Klaviyo ' + r.status + ': ' + (err.detail || 'no detail') + ' ' + pointer);
          showError(d.errorNetwork);
        });
      }, function () {
        // fetch itself failed: offline, DNS, blocked by an extension, CORS.
        busy(false);
        cfg.onError('network');
        showError(d.errorNetwork);
      });
    });
  }

  // =================== Footer ===================
  var footer = document.querySelector('[data-lm-signup="footer"]');
  if (footer && footer.querySelector('[data-lm-signup-posted]') && window.location.hash === '#ContactFooter') {
    markSubscribed(); // native fallback post just succeeded
  }
  wireForm({
    selector: '[data-lm-signup="footer"]',
    errorSel: '[data-lm-signup-error]',
    successSel: '[data-lm-signup-success]',
    customSource: 'Website footer',
    props: { signup_source: 'footer' },
    onSubmit: function () { track('footer_submit', { page_path: window.location.pathname }); },
    onSuccess: function (root) {
      track('footer_success', { page_path: window.location.pathname, mode: root.dataset.mode === 'double_optin' ? 'double_optin' : 'single' });
    },
    onError: function (reason) { track('footer_error', { page_path: window.location.pathname, reason: reason }); }
  });

  // =================== Popup ===================
  var root = document.getElementById('lm-popup');
  if (!root) return;

  var d = root.dataset;
  var hasMouse = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var minMs = num(d.minSeconds, 8) * 1000;
  var timeMs = num(d.timeSeconds, 20) * 1000;
  var scrollPct = num(d.scrollPercent, 50);
  var frequencyDays = num(d.frequencyDays, 30);
  var mode = d.mode === 'double_optin' ? 'double_optin' : 'single';

  var dialog = root.querySelector('.lm-popup__dialog');
  var errorEl = root.querySelector('#lm-popup-error');
  var posted = root.querySelector('[data-lm-popup-posted]');

  var lastFocused = null;
  var openedAt = 0;
  var triggered = false;
  var queued = false;
  var subscribed = false;
  var timers = [];

  // ---- Variant (only when B copy exists) ----
  var variant = 'A';
  if (d.variantB === 'true') {
    variant = get('localStorage', KEY_VARIANT);
    if (variant !== 'A' && variant !== 'B') {
      variant = Math.random() < 0.5 ? 'A' : 'B';
      set('localStorage', KEY_VARIANT, variant);
    }
    if (variant === 'B') {
      root.querySelectorAll('[data-b]').forEach(function (el) {
        if (el.getAttribute('data-b')) el.textContent = el.getAttribute('data-b');
      });
    }
  }

  function trackPopup(name, data) {
    data.variant = variant;
    track(name, data);
  }

  function klaviyoIdentified() {
    var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
    if (!m) return false;
    try {
      var o = JSON.parse(atob(decodeURIComponent(m[1])));
      return !!(o && (o.$email || o.$exchange_id));
    } catch (e) { return false; }
  }

  function suppressed() {
    if (designMode) return false;
    if (get('localStorage', KEY_SUBSCRIBED)) return true;
    if (get('sessionStorage', KEY_FROM_EMAIL) || klaviyoIdentified()) return true;
    var ts = parseInt(get('localStorage', KEY_DISMISSED), 10);
    return !!ts && (Date.now() - ts) / 86400000 < frequencyDays;
  }

  // ---- Dialog ----
  function focusables() {
    return Array.prototype.filter.call(
      dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.getClientRects().length > 0; }
    );
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close('esc'); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function activate() {
    lastFocused = document.activeElement;
    openedAt = Date.now();
    root.classList.add('is-open');
    root.removeAttribute('hidden');
    document.addEventListener('keydown', onKeydown);
    var f = focusables();
    if (f.length) f[0].focus();
  }

  function open(trigger) {
    if (root.classList.contains('is-open')) return;
    disarm();
    activate();
    trackPopup('popup_impression', { page_path: window.location.pathname, trigger: trigger });
  }

  function close(method) {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('hidden', '');
    document.removeEventListener('keydown', onKeydown);
    set('localStorage', KEY_DISMISSED, String(Date.now()));
    if (!subscribed) {
      trackPopup('popup_dismiss', { method: method, seconds_open: Math.round((Date.now() - openedAt) / 1000) });
    }
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  root.querySelectorAll('[data-lm-popup-close]').forEach(function (el) {
    el.addEventListener('click', function () { close(el.getAttribute('data-lm-popup-close')); });
  });

  // ---- Triggers ----
  function fire(trigger) {
    if (triggered) return;
    var wait = minMs - performance.now();
    if (wait > 0) {
      if (queued) return;
      queued = true;
      timers.push(setTimeout(function () { queued = false; fire(trigger); }, wait));
      return;
    }
    triggered = true;
    open(trigger);
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.scrollY / h) * 100 >= scrollPct) fire('scroll');
    });
  }

  function onExitIntent(e) {
    // Cursor left through the top edge (toward the tabs / close button).
    if (e.clientY <= 0 && !e.relatedTarget) { triggered = true; open('exit'); }
  }

  function disarm() {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExitIntent);
    timers.forEach(clearTimeout);
    timers = [];
  }

  function arm() {
    // Subscribed somewhere else on this page (the footer) → stand down.
    document.addEventListener('lm:subscribed', function () {
      if (!subscribed) { triggered = true; disarm(); }
    });
    if (hasMouse && d.desktopExit === 'true') {
      document.addEventListener('mouseout', onExitIntent);
      return;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    timers.push(setTimeout(function () { fire('time'); }, Math.max(timeMs - performance.now(), 0)));
  }

  // Klaviyo's own answer to "do we know this browser?" (clicked a Klaviyo
  // email, or signed up before) — covers what the __kla_id cookie check can
  // miss. The onsite embed defines window.klaviyo as a queueing proxy in
  // <head>, so this works before klaviyo.js loads. Nothing waits on it: the
  // triggers are already armed and stand down if the answer is yes. If it only
  // arrives after the popup has opened, the popup stays open.
  function checkKlaviyoIdentified() {
    var k = window.klaviyo;
    if (!k || typeof k.isIdentified !== 'function') return;
    try {
      Promise.resolve(k.isIdentified()).then(function (identified) {
        if (identified === true && !root.classList.contains('is-open')) {
          triggered = true;
          disarm();
        }
      }, function () {});
    } catch (e) {}
  }

  // ---- Submit ----
  wireForm({
    selector: '#lm-popup',
    errorSel: '#lm-popup-error',
    successSel: '[data-lm-popup-success]',
    customSource: 'Website popup',
    props: { signup_source: 'popup', signup_variant: variant },
    onSubmit: function () { trackPopup('popup_submit', { page_path: window.location.pathname }); },
    onSuccess: function () {
      subscribed = true; // closing after this isn't a "dismiss"
      trackPopup('popup_success', { page_path: window.location.pathname, mode: mode });
    },
    onError: function (reason) {
      trackPopup('popup_error', { page_path: window.location.pathname, reason: reason });
    }
  });

  // ---- Start ----
  // `form.posted_successfully?` is true after ANY customer form post (the footer
  // signup too); Shopify's redirect keeps the posting form's id as the hash.
  var postedHere = window.location.hash === '#LMPopupForm';
  if (posted) {
    if (postedHere) {
      // Native fallback post just succeeded (page reloaded into the success state).
      subscribed = true;
      markSubscribed();
      activate();
      trackPopup('popup_success', { mode: 'single' });
    }
    // Otherwise the footer just subscribed them — don't show the popup.
  } else if (postedHere && errorEl && !errorEl.hidden) {
    activate(); // native fallback post came back with an error — show it
  } else if (designMode) {
    // Theme editor: preview on section select, and once shortly after load.
    document.addEventListener('shopify:section:select', function (e) {
      if (root.closest('[id^="shopify-section-"]') === e.target) activate();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (root.closest('[id^="shopify-section-"]') === e.target) close('close');
    });
    setTimeout(function () { open('time'); }, 1500);
  } else if (!suppressed()) {
    arm();
    checkKlaviyoIdentified();
  }
})();
